import { Injectable } from '@angular/core';
import {ShipmentPrefAddress} from 'src/app/shared/common-components/common-structure';
import { DataService } from '../data.service';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, combineLatest, of } from 'rxjs';
import {switchMap, map, catchError, tap, filter} from 'rxjs/operators';
import { NameAndAddressService } from '../name-and-address/name-and-address.service';
import { NameAndAddressRecordType } from 'src/app/shared/common-components/AppConstants';
import { AlertService } from '../alert/alert.service';
import { LoadingService } from '../loading/loading.service';

@Injectable({
  providedIn: 'root'
})
export class ShipmentPreferredAddressesService extends DataService {

  public observable$: any;
  private baseURI = this.apiUri + '/shipmentprefaddresses/';
  private shipmentPreferredAddressRequest = new BehaviorSubject<any>(null);

  allStates$ = this.nameAndAddressService.allStates$;
  allCountriesList$ = this.nameAndAddressService.allCountriesList$;
  private shipmentPreferredAddressList = this.shipmentPreferredAddressRequest.pipe(
    filter((request: any) => request),
    switchMap((request: any) => {
      return this.getShipmentPrefAddressesById(request.userId, request.orgId, request.recordType, request.zipCode);
    }));

  shipmentPreferredAddresses$ = combineLatest([this.shipmentPreferredAddressList, this.allCountriesList$, this.allStates$])
    .pipe(
      map(([addresses, countries, states]) => {
        addresses.map(address => {
          address['state'] = states.find(state => state.stateCountryId === address.stateId) ?
            states.find(state => state.stateCountryId === address.stateId).stateCountryName : '';
          address['country'] =  countries.find(ctry => ctry.stateCountryId === address.countryId) ?
            countries.find(ctry => ctry.stateCountryId === address.countryId).stateCountryName : '';
          address['displayAddressType'] = address.recordType === NameAndAddressRecordType.ORIGIN ? 'Origin Address'
            : address.recordType === NameAndAddressRecordType.BILL_TO ? 'Bill To Address'
              : address.recordType === NameAndAddressRecordType.SHIPPER ? 'Shipper Address'
                : address.recordType === NameAndAddressRecordType.DESTINATION ? 'Destination Address'
                  : address.recordType === NameAndAddressRecordType.CONSIGNEE ? 'Consignee Address'
                    : 'Issuing Office Address';
        });
        return addresses;
      }));


  constructor(http: HttpClient, private nameAndAddressService: NameAndAddressService,
    private alertService: AlertService, private loader: LoadingService) {
    super(http);
    this.setUri(this.baseURI);
  }

  // Function to save name and addresses
  saveShipmentPrefAddressesById(
    shipmentPrefAddress: ShipmentPrefAddress
  ): Observable<any> {
    const uri = this.baseURI + 'saveShipmentPrefAddress';
    this.setUri(uri);
    this.loader.show();
    this.reverseMapShipmentPreferredAddress(shipmentPrefAddress);
    return this.update(shipmentPrefAddress).pipe(tap(() => {
      this.loader.hide();
      this.alertService.success('Successfully saved the preferred shipment Address');
    }));
  }

  // Function to get name and addresses for Origin or Destination
  getShipmentPrefAddressesById(
    userId: number,
    orgId: number,
    recordType: string,
    zipCode?: string
  ): Observable<ShipmentPrefAddress[]> {
    let uri = this.baseURI + orgId + '/orgId/' + recordType + '/recordType';
    this.loader.show();
    if (zipCode) {
      uri = uri + '/' + zipCode + '/zipCode';
    }
    this.setUri(uri);
    return this.get().pipe(map((addresses) => addresses.map((address: any) => this.mapShipmentPreferredAddress(address))),
      catchError((e) => of([] as ShipmentPrefAddress[])), tap(() => this.loader.hide()));
  }

  // Function to save name and addresses
  deleteShipmentPrefAddressesById(
    shipmentPrefAddressesId: number
  ): Observable<any> {
    return this.http.put(
      this.baseURI +
      'deleteShipmentPrefAddress/' +
      shipmentPrefAddressesId +
      '/shipmentPrefAddressesId/delete',
      shipmentPrefAddressesId
    );
  }

  updateShipmentPreferredAddressRequest(userId: number, orgId: number, recordType: string, zipCode?: string) {
    this.shipmentPreferredAddressRequest.next({ userId, orgId,  recordType, zipCode });
  }

  mapShipmentPreferredAddress(address: any) {
    address.stateId = address.stateID;
    address.phoneExt = address.phoneEXT;
    address.countryId = address.countryID;
    return address as ShipmentPrefAddress;
  }

  reverseMapShipmentPreferredAddress(address: ShipmentPrefAddress) {
    address['stateID'] = address.stateId;
    address['countryID'] = address.countryId;
    address['phoneEXT'] = address.phoneExt;
  }

}
